import { apiClient } from './client';

export interface UserInfo {
  id: string;
  email: string;
  display_name: string;
  is_platform_admin: boolean;
  roles: string[]; // keep for backward compat with existing role-based routes
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  user: UserInfo;
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  return apiClient.post<AuthResponse>('/auth/login', { email, password });
}

export async function register(email: string, password: string, displayName: string): Promise<AuthResponse> {
  return apiClient.post<AuthResponse>('/auth/register', { email, password, display_name: displayName });
}

export async function refreshToken(refreshToken: string): Promise<AuthResponse> {
  return apiClient.post<AuthResponse>('/auth/refresh', { refresh_token: refreshToken });
}

export async function logout(refreshToken: string): Promise<void> {
  return apiClient.post<void>('/auth/logout', { refresh_token: refreshToken });
}

export async function getMe(): Promise<UserInfo> {
  return apiClient.get<UserInfo>('/auth/me');
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiClient.post<void>('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

export async function forgotPassword(email: string): Promise<void> {
  return apiClient.post<void>('/auth/forgot-password', { email });
}

export type OtpPurpose = 'login' | 'register' | 'reset_password';

export interface OtpRequestResponse {
  message: string;
  expires_in: number;
  /** Only present in development mode (SMS not configured on the server). */
  otp?: string;
}

export interface OtpVerifyResponse {
  verified: boolean;
  user_exists: boolean;
  /** Present when the OTP purpose was `login` and the phone belongs to a user. */
  auth: AuthResponse | null;
}

export async function requestOtp(
  phoneNumber: string,
  countryCode: string,
  purpose: OtpPurpose = 'login',
): Promise<OtpRequestResponse> {
  return apiClient.post<OtpRequestResponse>('/auth/otp/request', {
    phone_number: phoneNumber,
    country_code: countryCode,
    purpose,
  });
}

export async function verifyOtp(
  phoneNumber: string,
  code: string,
  countryCode: string,
  purpose: OtpPurpose = 'login',
): Promise<OtpVerifyResponse> {
  return apiClient.post<OtpVerifyResponse>('/auth/otp/verify', {
    phone_number: phoneNumber,
    code,
    country_code: countryCode,
    purpose,
  });
}

export async function phoneRegister(
  phoneNumber: string,
  countryCode: string,
  displayName: string,
): Promise<AuthResponse> {
  return apiClient.post<AuthResponse>('/auth/phone/register', {
    phone_number: phoneNumber,
    country_code: countryCode,
    display_name: displayName,
  });
}

export async function resetPasswordSms(
  phoneNumber: string,
  countryCode: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return apiClient.post<void>('/auth/password/reset-sms', {
    phone_number: phoneNumber,
    country_code: countryCode,
    code,
    new_password: newPassword,
  });
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  return apiClient.post<void>('/auth/reset-password', {
    token,
    new_password: newPassword,
  });
}

export async function exportMyData(): Promise<Blob> {
  return apiClient.blob('/users/me/export');
}

export async function deleteMyAccount(): Promise<void> {
  return apiClient.delete<void>('/users/me');
}

export async function getGoogleOAuthUrl(): Promise<{ url: string; state: string }> {
  return apiClient.get<{ url: string; state: string }>('/auth/oauth/google/url');
}

export async function googleOAuthCallback(code: string, state: string): Promise<AuthResponse> {
  return apiClient.post<AuthResponse>('/auth/oauth/google/callback', { code, state });
}
